import { Link } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";

// TikTok icon (not available in lucide-react)
const TikTokIcon = () => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className="w-5 h-5"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "Destinations", path: "/destinations" },
    { name: "Services", path: "/services" },
    { name: "Products", path: "/products" },
  ];

  const supportLinks = [
    { name: "Membership", path: "/membership" },
    { name: "About Us", path: "/about" },
    { name: "Contact", path: "/membership" },
    { name: "FAQ", path: "/about" },
  ];

  const destinations = [
    { name: "Bali", path: "/destinations" },
    { name: "Komodo Island", path: "/destinations" },
    { name: "Raja Ampat", path: "/destinations" },
    { name: "Lake Toba", path: "/destinations" },
  ];

  return (
    <footer 
      data-testid="footer"
      className="relative bg-[#0E2954] text-white pt-24 pb-8"
    >
      <div className="footer-wave" />
      
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div>
            <Link 
              to="/" 
              data-testid="footer-logo"
              className="flex items-center gap-3 mb-4"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_build-sites-32/artifacts/cg4su8qh_photo_2026-04-05_22-55-36.jpg" 
                alt="Archipelago by Av Logo"
                className="w-14 h-14 object-contain"
              />
              <span className="font-['Playfair_Display'] text-2xl font-semibold">
                Archipelago<span className="text-[#D92525]"> by Av</span>
              </span>
            </Link>
            
            {/* Collaboration */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-white/60 text-sm">in collaboration with</span>
              <img 
                src="https://customer-assets.emergentagent.com/job_build-sites-32/artifacts/3ppf51qn_Logo%20Wake%20Up%20Call%20Final.PNG" 
                alt="Wake Up Call Logo"
                className="h-20 object-contain"
              />
            </div>
            <p className="text-white/70 mb-4 leading-relaxed">
              Your gateway to discovering the beauty of Indonesia. 
              From pristine beaches to ancient temples, we bring you 
              authentic experiences across 17,000 islands.
            </p>
            
            {/* Contact Info */}
            <div className="mb-6 space-y-2">
              <p className="text-white/90 text-sm">
                <span className="text-white font-medium">Contact:</span> Admin +62-851-2150-1670 (WA)
              </p>
              <p className="text-white/90 text-sm">
                <span className="text-white font-medium">Email:</span>{" "}
                <a href="mailto:Avnerkrissy@gmail.com" className="hover:text-white underline">
                  Avnerkrissy@gmail.com
                </a>
              </p>
            </div>
            
            {/* Social Links */}
            <div className="flex gap-4">
              <a 
                href="https://instagram.com/archipelagobyav" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="social-icon"
                aria-label="Instagram - @archipelagobyav"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://facebook.com/ArchipelagoAv" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-facebook-link"
                className="social-icon"
                aria-label="Facebook - Archipelago Av"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://tiktok.com" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-tiktok-link"
                className="social-icon"
                aria-label="TikTok"
              >
                <TikTokIcon />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-['Playfair_Display']">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    data-testid={`footer-${link.name.toLowerCase().replace(' ', '-')}-link`}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-['Playfair_Display']">
              Support
            </h4>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    data-testid={`footer-${link.name.toLowerCase().replace(' ', '-')}-link`}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-['Playfair_Display']">
              Top Destinations
            </h4>
            <ul className="space-y-3">
              {destinations.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    data-testid={`footer-${link.name.toLowerCase().replace(' ', '-')}-link`}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              {currentYear} Archipelago by Av. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-white/50">
              <a href="#" className="hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
